import React from "react";
import Reveal from "@/components/Reveal";

/**
 * Hairline-divided "Verified & Compliant" strip.
 * Pairs the real Eurofins certification mark (treated to blend with the
 * editorial palette) with text-only chips for cGMP and FDA Registered Facility.
 *
 * Use `tone="dark"` on cream/light backgrounds, `tone="light"` on ink backgrounds.
 */
export default function CredentialsStrip({ tone = "dark" }) {
  const isDark = tone === "dark";
  const ink = isDark ? "#0f0f10" : "#faf9f5";
  const muted = isDark ? "rgba(15,15,16,0.55)" : "rgba(250,249,245,0.55)";
  const line = isDark ? "rgba(15,15,16,0.18)" : "rgba(250,249,245,0.20)";

  return (
    <Reveal>
      <div
        className="w-full"
        data-testid="credentials-strip"
        style={{ borderTop: `1px solid ${line}`, borderBottom: `1px solid ${line}` }}
      >
        <div className="max-w-site mx-auto px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-center md:items-stretch gap-6 md:gap-0">
          {/* Eyebrow */}
          <div
            className="label !text-[10px] md:w-[180px] md:flex-shrink-0 md:flex md:items-center"
            style={{ color: muted, letterSpacing: "0.28em" }}
          >
            Verified & Compliant
          </div>

          {/* Eurofins logo cell */}
          <div
            className="flex items-center gap-3 md:px-8 md:flex-1 justify-center md:justify-start"
            style={{ borderLeft: `1px solid ${line}` }}
          >
            <img
              src="/assets/cert/eurofins.png"
              alt="Eurofins Certified"
              className="h-8 w-auto"
              style={{
                // Mute the saturation/brightness so the brand mark reads as a
                // clinical credential rather than a marketing badge.
                filter: isDark
                  ? "grayscale(100%) brightness(0.35) contrast(1.05)"
                  : "grayscale(100%) brightness(2.4) contrast(0.95) invert(0)",
                opacity: 0.78,
              }}
              loading="lazy"
            />
            <div className="leading-tight">
              <div
                className="label !text-[10px]"
                style={{ color: ink, letterSpacing: "0.22em" }}
              >
                Eurofins Certified
              </div>
              <div
                className="text-[10px] mt-0.5"
                style={{ color: muted, letterSpacing: "0.04em" }}
              >
                Independent purity & potency testing
              </div>
            </div>
          </div>

          {/* cGMP cell */}
          <div
            className="flex items-center md:px-8 md:flex-1 justify-center md:justify-start"
            style={{ borderLeft: `1px solid ${line}` }}
          >
            <div className="leading-tight">
              <div
                className="label !text-[10px]"
                style={{ color: ink, letterSpacing: "0.22em" }}
              >
                cGMP Certified
              </div>
              <div
                className="text-[10px] mt-0.5"
                style={{ color: muted, letterSpacing: "0.04em" }}
              >
                Current Good Manufacturing Practice — US facility
              </div>
            </div>
          </div>

          {/* FDA cell */}
          <div
            className="flex items-center md:px-8 md:flex-1 justify-center md:justify-start"
            style={{ borderLeft: `1px solid ${line}` }}
          >
            <div className="leading-tight">
              <div
                className="label !text-[10px]"
                style={{ color: ink, letterSpacing: "0.22em" }}
              >
                FDA Registered Facility
              </div>
              <div
                className="text-[10px] mt-0.5"
                style={{ color: muted, letterSpacing: "0.04em" }}
              >
                Manufactured under FDA oversight
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
